/**
 * Загрузка данных экрана: ожидание, готово, не получилось.
 *
 * **Рядом живёт `query.ts` с `useRead`, и это временно.** Оба решают одну
 * задачу и разошлись только тем, что `useRead` умеет один GET по адресу, а
 * здесь на вход идёт любая функция загрузки. Разница нужна ровно в одном месте
 * и по существу: раздел, до которого роль не дотягивается, обязан вернуть
 * «данных нет и не будет» (`null`), а не остаться в вечном ожидании. У
 * `useRead` признак «грузить нечего» — `path = null` — оставляет экран в
 * состоянии `loading`, то есть в скелете навсегда.
 *
 * Сводить их надо в одну функцию, и делать это должен тот, кто может проверить
 * все экраны сразу: `useRead` держит витрину и организацию, `useLoad` — бренды,
 * ядро и прогоны. Развилка описана здесь, чтобы следующий не решил, что вторая
 * копия появилась по недосмотру.
 *
 * Три состояния повторяются на каждом экране кабинета, и каждый раз это один и
 * тот же эффект с `AbortController`. Собран он здесь по двум причинам: во-первых,
 * забытая отмена в разделе, который человек пролистывает за секунду, ставит
 * ответ мёртвого запроса поверх свежего; во-вторых, повтор после ошибки должен
 * работать одинаково везде, иначе половина экранов остаётся без кнопки
 * «Повторить».
 *
 * Ключ вместо массива зависимостей — намеренно. Область работы кабинета всегда
 * описывается строкой (бренд, фильтры, страница), и строковый ключ читается в
 * коде экрана как то, при смене чего данные устарели.
 *
 * **Возврат в ожидание считается, а не выставляется.** Ответ хранится вместе с
 * ключом, под который его получили; как только ключ сменился, экран показывает
 * ожидание, потому что данные к новому ключу ещё не пришли. Так на смене бренда
 * не бывает кадра, где новый заголовок стоит над старыми числами, — и при этом
 * в теле эффекта нет ни одного `setState`, то есть нет и каскада перерисовок.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export type Load<T> =
  { kind: 'loading' } | { kind: 'ready'; data: T } | { kind: 'failed'; error: unknown };

export interface Loaded<T> {
  state: Load<T>;
  /** Перечитать с сервера. Экран возвращается в ожидание. */
  reload: () => void;
  /**
   * Положить свежие данные, полученные ответом на изменение, — без второго
   * запроса. Список после добавления строки знает свой новый состав сам.
   */
  put: (data: T) => void;
}

const WAITING = { kind: 'loading' } as const;

export function useLoad<T>(
  key: string,
  fetcher: (signal: AbortSignal) => Promise<T>,
): Loaded<T> {
  const [attempt, setAttempt] = useState(0);
  const [answer, setAnswer] = useState<{ token: string; load: Load<T> } | null>(null);

  const token = `${key}#${attempt}`;

  // Функция загрузки пересоздаётся каждой перерисовкой (она замыкает пропсы), и
  // держать её в зависимостях эффекта значит грузить экран бесконечно.
  // Актуальная версия живёт в ссылке, а перезапуск задаётся ключом и попыткой.
  const latest = useRef(fetcher);
  const current = useRef(token);
  useEffect(() => {
    latest.current = fetcher;
    current.current = token;
  });

  useEffect(() => {
    const abort = new AbortController();

    latest
      .current(abort.signal)
      .then((data) => {
        if (abort.signal.aborted) return;
        setAnswer({ token, load: { kind: 'ready', data } });
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setAnswer({ token, load: { kind: 'failed', error } });
      });

    return () => abort.abort();
  }, [token]);

  const reload = useCallback(() => setAttempt((value) => value + 1), []);
  // Зовётся из обработчика события, а не из отрисовки, поэтому ключ берётся из
  // ссылки: положить ответ надо под тот ключ, который на экране прямо сейчас.
  const put = useCallback(
    (data: T) => setAnswer({ token: current.current, load: { kind: 'ready', data } }),
    [],
  );

  // Ответ, полученный под другой ключ, — это ответ про другой экран.
  return { state: answer?.token === token ? answer.load : WAITING, reload, put };
}
